import { useState } from 'react';
import { Values } from '@lib/interfaces/form';
import {
  PaginationParams,
  PAGINATION_PARAMS,
  OrderingParams,
  ORDERING_PARAMS,
  SearchStringState,
  PaginationState,
  OrderingState,
} from '@lib/enums/urls';
import { TableTabs } from '../enums';

export type FilterState = [Values, (v: Values) => void];
export type ActiveTabState = [TableTabs, (v: TableTabs) => void];

export interface ModalTableMultiselectStates {
  // Tab state
  activeTabState: ActiveTabState;
  // All tab states
  searchState: SearchStringState;
  paginationState: PaginationState;
  orderingState: OrderingState;
  filterState: FilterState;
  // Selected tab states
  searchStateSelected: SearchStringState;
  paginationStateSelected: PaginationState;
  orderingStateSelected: OrderingState;
  filterStateSelected: FilterState;
}

/**
 * Custom hook for managing states for both tabs in ModalTableMultiselect
 * Provides reusable state management for active tab, search, pagination, ordering, and filters
 * for both All and Selected tabs
 */
export function useModalTableMultiselectStates({
  filterStateDefault = {},
  selected,
  selectionLimit,
}: {
  filterStateDefault: Values;
  selected?: string[];
  selectionLimit?: number;
}): ModalTableMultiselectStates {
  // Active tab state — single-select pickers have no Selected tab, so they
  // always start on All.
  const activeTabState = useState<TableTabs>(
    selected?.length && selectionLimit !== 1
      ? TableTabs.Selected
      : TableTabs.All,
  );

  // States for All tab
  const searchState = useState<string>('');
  const paginationState = useState<PaginationParams>(PAGINATION_PARAMS);
  const orderingState = useState<OrderingParams>(ORDERING_PARAMS);
  const filterState = useState<Values>(filterStateDefault);

  // States for Selected tab
  const searchStateSelected = useState<string>('');
  const paginationStateSelected = useState<PaginationParams>(PAGINATION_PARAMS);
  const orderingStateSelected = useState<OrderingParams>(ORDERING_PARAMS);
  const filterStateSelected = useState<Values>(filterStateDefault);

  return {
    activeTabState,
    searchState,
    paginationState,
    orderingState,
    filterState,
    searchStateSelected,
    paginationStateSelected,
    orderingStateSelected,
    filterStateSelected,
  };
}
